'use client'

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'designSystem/components/Command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'designSystem/components/Popover'
import { cn } from 'designSystem/lib/utils'
import { CheckIcon, ChevronDownIcon } from 'lucide-react'
import * as React from 'react'

type ComboboxContextValue = {
  value: string | undefined
  onValueChange: (value: string) => void
  open: boolean
  onOpenChange: (open: boolean) => void
  // value -> label map populated by ComboboxItem on mount so the trigger can
  // render the selected label without re-walking children.
  registerLabel: (value: string, label: React.ReactNode) => void
  labels: Map<string, React.ReactNode>
}

const ComboboxContext = React.createContext<ComboboxContextValue | null>(null)

function useComboboxContext() {
  const ctx = React.useContext(ComboboxContext)
  if (!ctx) {
    throw new Error('Combobox components must be used within <Combobox>')
  }
  return ctx
}

function Combobox({
  value: valueProp,
  defaultValue,
  onValueChange,
  open: openProp,
  defaultOpen,
  onOpenChange,
  children,
}: {
  value?: string
  defaultValue?: string
  onValueChange?: (value: string) => void
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  children?: React.ReactNode
}) {
  const [valueState, setValueState] = React.useState<string | undefined>(
    defaultValue
  )
  const value = valueProp ?? valueState

  const [openState, setOpenState] = React.useState(defaultOpen ?? false)
  const open = openProp ?? openState

  const labelsRef = React.useRef(new Map<string, React.ReactNode>())
  const [, forceRender] = React.useReducer((n: number) => n + 1, 0)

  const registerLabel = React.useCallback(
    (v: string, label: React.ReactNode) => {
      const existing = labelsRef.current.get(v)
      if (existing !== label) {
        labelsRef.current.set(v, label)
        forceRender()
      }
    },
    []
  )

  const handleValueChange = React.useCallback(
    (next: string) => {
      if (valueProp === undefined) setValueState(next)
      onValueChange?.(next)
    },
    [valueProp, onValueChange]
  )

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (openProp === undefined) setOpenState(next)
      onOpenChange?.(next)
    },
    [openProp, onOpenChange]
  )

  const ctx = React.useMemo<ComboboxContextValue>(
    () => ({
      value,
      onValueChange: handleValueChange,
      open,
      onOpenChange: handleOpenChange,
      registerLabel,
      labels: labelsRef.current,
    }),
    [value, handleValueChange, open, handleOpenChange, registerLabel]
  )

  return (
    <ComboboxContext.Provider value={ctx}>
      <Popover open={open} onOpenChange={handleOpenChange}>
        {children}
      </Popover>
    </ComboboxContext.Provider>
  )
}

function ComboboxTrigger({
  className,
  size = 'default',
  children,
  placeholder,
  ...props
}: React.ComponentProps<'button'> & {
  size?: 'xs' | 'sm' | 'default'
  placeholder?: React.ReactNode
}) {
  const { value, labels } = useComboboxContext()
  // `children` wins, then the label ComboboxItem registered on mount, then
  // nothing. We can't gate on a registered label to decide "has value" —
  // items only mount when the popover opens, so before the first open the
  // trigger would otherwise look empty even when `value` is set.
  const registeredLabel = value !== undefined ? labels.get(value) : undefined
  const displayLabel = children ?? registeredLabel
  const hasValue = value !== undefined && displayLabel !== undefined

  return (
    <PopoverTrigger asChild>
      <button
        type='button'
        data-slot='combobox-trigger'
        data-size={size}
        data-placeholder={hasValue ? undefined : ''}
        className={cn(
          "flex w-fit items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 data-[placeholder]:text-muted-foreground data-[size=default]:h-9 data-[size=sm]:h-8 data-[size=xs]:h-6 data-[size=xs]:gap-1 data-[size=xs]:px-2 data-[size=xs]:py-1 data-[size=xs]:text-xs dark:bg-input/30 dark:hover:bg-input/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground",
          className
        )}
        {...props}>
        <span
          data-slot='combobox-value'
          className='line-clamp-1 flex items-center gap-2'>
          {hasValue ? displayLabel : placeholder}
        </span>
        <ChevronDownIcon className='size-4 opacity-50' />
      </button>
    </PopoverTrigger>
  )
}

function ComboboxContent({
  className,
  children,
  align = 'start',
  sideOffset = 4,
  ...props
}: React.ComponentProps<typeof PopoverContent>) {
  return (
    <PopoverContent
      data-slot='combobox-content'
      align={align}
      sideOffset={sideOffset}
      className={cn(
        'w-[var(--radix-popover-trigger-width)] min-w-[8rem] gap-0 p-0',
        className
      )}
      {...props}>
      <Command>{children}</Command>
    </PopoverContent>
  )
}

function ComboboxInput({
  ...props
}: React.ComponentProps<typeof CommandInput>) {
  return <CommandInput data-slot='combobox-input' {...props} />
}

function ComboboxList({ ...props }: React.ComponentProps<typeof CommandList>) {
  return <CommandList data-slot='combobox-list' {...props} />
}

function ComboboxGroup({
  ...props
}: React.ComponentProps<typeof CommandGroup>) {
  return <CommandGroup data-slot='combobox-group' {...props} />
}

function ComboboxEmpty({
  ...props
}: React.ComponentProps<typeof CommandEmpty>) {
  return <CommandEmpty data-slot='combobox-empty' {...props} />
}

function ComboboxItem({
  className,
  value,
  children,
  label,
  onSelect,
  ...props
}: Omit<React.ComponentProps<typeof CommandItem>, 'value'> & {
  value: string
  /** Override the display label rendered in the trigger when this item is selected. Defaults to `children`. */
  label?: React.ReactNode
}) {
  const ctx = useComboboxContext()
  const displayLabel = label ?? children

  React.useEffect(() => {
    ctx.registerLabel(value, displayLabel)
  }, [ctx, value, displayLabel])

  const isSelected = ctx.value === value

  return (
    <CommandItem
      data-slot='combobox-item'
      value={value}
      onSelect={(currentValue) => {
        ctx.onValueChange(currentValue)
        ctx.onOpenChange(false)
        onSelect?.(currentValue)
      }}
      className={cn('pr-8', className)}
      {...props}>
      {children}
      <CheckIcon
        className={cn(
          'absolute right-2 size-4',
          isSelected ? 'opacity-100' : 'opacity-0'
        )}
      />
    </CommandItem>
  )
}

export {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
}
